/**
 * Blog SEO metadata and structured-data builders used by blog routes.
 *
 * What: Generates canonical URLs, Open Graph/Twitter tag data, and JSON-LD payloads for `/blog` and `/blog/:slug`.
 * Why: Keeps blog SEO logic consistent and centralized so listing/article pages do not duplicate metadata rules.
 * When: Update when blog SEO requirements, social preview fields, or schema.org payload contracts change.
 */

export interface BlogPostInput {
  slug?: string;
  routePath?: string;
  title: string;
  description?: string;
  excerpt?: string;
  publishDate: string;
  updatedDate?: string;
  reviewedDate?: string;
  authorName?: string;
  authorType?: string;
  authorRole?: string;
  authorCredentials?: string;
  authorUrl?: string;
  reviewerName?: string;
  reviewerType?: string;
  reviewerRole?: string;
  reviewerCredentials?: string;
  reviewerUrl?: string;
  publisherDescription?: string;
  expertiseAreas?: readonly string[];
  primaryCtaPath?: string;
  primaryCtaLabel?: string;
  primaryCtaDescription?: string;
  readingTimeMinutes?: number;
  category?: string;
  audience?: string;
  tags?: readonly string[];
}

interface SchemaEntityInput {
  name?: string;
  type?: string;
  description?: string;
  jobTitle?: string;
  url?: string;
}

interface SchemaEntity {
  '@type': string;
  name: string;
  description?: string;
  jobTitle?: string;
  url?: string;
  logo?: { '@type': string; url: string };
}

interface OpenGraphMeta {
  type: string;
  title: string;
  description: string;
  url: string;
  image: string;
  imageAlt: string;
  siteName: string;
  locale: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleReviewedTime?: string;
  articleSection?: string;
  articleTags?: string[];
}

interface TwitterMeta {
  card: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

export interface BlogListingSeoMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
  openGraph: OpenGraphMeta;
  twitter: TwitterMeta;
  jsonLd: {
    itemList: Record<string, unknown>;
    breadcrumb: Record<string, unknown>;
    blog: Record<string, unknown>;
  };
}

export interface BlogArticleSeoMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
  openGraph: OpenGraphMeta;
  twitter: TwitterMeta;
  jsonLd: {
    blogPosting: Record<string, unknown>;
    breadcrumb: Record<string, unknown>;
    faqPage?: Record<string, unknown>;
  };
}

export interface BlogFaqPair {
  question: string;
  answer: string;
}

const DEFAULT_SITE_ORIGIN = 'https://deepthink.sg';
const DEFAULT_BLOG_SOCIAL_IMAGE_PATH = '/og-image.png';

export const BLOG_LISTING_DESCRIPTION =
  'Study strategies, exam guides and learning advice for Mathematics, Secondary English and Chemistry — by the DeepThink teaching team.';

const BLOG_LISTING_TITLE_SUFFIX = 'Blog | Learning and Exam Strategy Guides';

function normalizeSiteOrigin(siteOrigin: string | undefined): string {
  if (typeof siteOrigin !== 'string' || !siteOrigin.trim()) {
    return DEFAULT_SITE_ORIGIN;
  }

  return siteOrigin.trim().replace(/\/+$/, '');
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

function buildAbsoluteUrl(siteOrigin: string, routeOrUrl: string): string {
  if (isAbsoluteUrl(routeOrUrl)) {
    return routeOrUrl;
  }

  if (typeof routeOrUrl !== 'string' || !routeOrUrl.trim()) {
    return siteOrigin;
  }

  const normalizedPath = routeOrUrl.startsWith('/')
    ? routeOrUrl
    : `/${routeOrUrl}`;
  return `${siteOrigin}${normalizedPath}`;
}

function buildSchemaEntity(
  {
    name,
    type = 'Organization',
    description,
    jobTitle,
    url,
  }: SchemaEntityInput,
  siteOrigin: string
): SchemaEntity | null {
  if (typeof name !== 'string' || !name.trim()) {
    return null;
  }

  const entity: SchemaEntity = {
    '@type': type,
    name: name.trim(),
  };

  if (typeof description === 'string' && description.trim()) {
    entity.description = description.trim();
  }

  if (type === 'Person' && typeof jobTitle === 'string' && jobTitle.trim()) {
    entity.jobTitle = jobTitle.trim();
  }

  if (typeof url === 'string' && url.trim()) {
    entity.url = buildAbsoluteUrl(siteOrigin, url.trim());
  }

  return entity;
}

function resolveSocialImageUrl({
  siteOrigin,
  socialImageUrl,
}: {
  siteOrigin: string;
  socialImageUrl?: string;
}): string {
  if (typeof socialImageUrl === 'string' && socialImageUrl.trim()) {
    return buildAbsoluteUrl(siteOrigin, socialImageUrl.trim());
  }

  return buildAbsoluteUrl(siteOrigin, DEFAULT_BLOG_SOCIAL_IMAGE_PATH);
}

function getBlogArticleDescription(blogPost?: BlogPostInput | null): string {
  if (
    typeof blogPost?.description === 'string' &&
    blogPost.description.trim()
  ) {
    return blogPost.description.trim();
  }

  if (typeof blogPost?.excerpt === 'string' && blogPost.excerpt.trim()) {
    return blogPost.excerpt.trim();
  }

  return 'Read this DeepThink blog article for practical math learning strategies.';
}

function normalizeBlogRoutePath(blogPost?: BlogPostInput | null): string {
  if (typeof blogPost?.routePath === 'string' && blogPost.routePath.trim()) {
    return blogPost.routePath.startsWith('/')
      ? blogPost.routePath.trim()
      : `/${blogPost.routePath.trim()}`;
  }

  return `/blog/${blogPost?.slug || ''}`;
}

export function buildBlogListingSeoMetadata({
  brandName = 'DeepThink',
  siteOrigin = DEFAULT_SITE_ORIGIN,
  blogPosts = [] as BlogPostInput[],
  socialImageUrl,
}: {
  brandName?: string;
  siteOrigin?: string;
  blogPosts?: BlogPostInput[];
  socialImageUrl?: string;
} = {}): BlogListingSeoMetadata {
  const normalizedSiteOrigin = normalizeSiteOrigin(siteOrigin);
  const canonicalUrl = `${normalizedSiteOrigin}/blog`;
  const title = `${brandName} ${BLOG_LISTING_TITLE_SUFFIX}`;
  const resolvedSocialImageUrl = resolveSocialImageUrl({
    siteOrigin: normalizedSiteOrigin,
    socialImageUrl,
  });
  const normalizedPosts = Array.isArray(blogPosts) ? blogPosts : [];

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: normalizedPosts.map((post, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: buildAbsoluteUrl(normalizedSiteOrigin, normalizeBlogRoutePath(post)),
      name: post.title,
    })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: normalizedSiteOrigin,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: canonicalUrl,
      },
    ],
  };

  // schema.org/Blog with per-post BlogPosting stubs. Comprehensive structured
  // data raises AI-engine citation likelihood; the stubs let an engine parse
  // the listing as a first-class blog collection (headline + canonical URL +
  // freshness dates) without visiting every article.
  const blogJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${canonicalUrl}#blog`,
    name: `${brandName} Blog`,
    description: BLOG_LISTING_DESCRIPTION,
    url: canonicalUrl,
    inLanguage: 'en-SG',
    publisher: {
      '@type': 'Organization',
      name: brandName,
      url: normalizedSiteOrigin,
    },
    blogPost: normalizedPosts.map(post => {
      const description =
        post.description?.trim() || post.excerpt?.trim() || undefined;
      return {
        '@type': 'BlogPosting',
        headline: post.title,
        url: buildAbsoluteUrl(
          normalizedSiteOrigin,
          normalizeBlogRoutePath(post)
        ),
        datePublished: post.publishDate,
        dateModified: post.updatedDate || post.publishDate,
        ...(description ? { description } : {}),
      };
    }),
  };

  return {
    title,
    description: BLOG_LISTING_DESCRIPTION,
    canonicalUrl,
    openGraph: {
      type: 'website',
      title,
      description: BLOG_LISTING_DESCRIPTION,
      url: canonicalUrl,
      image: resolvedSocialImageUrl,
      imageAlt: `${brandName} blog preview`,
      siteName: brandName,
      locale: 'en_SG',
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description: BLOG_LISTING_DESCRIPTION,
      image: resolvedSocialImageUrl,
      imageAlt: `${brandName} blog preview`,
    },
    jsonLd: {
      itemList: itemListJsonLd,
      breadcrumb: breadcrumbJsonLd,
      blog: blogJsonLd,
    },
  };
}

// Matches headings that START with an FAQ-style keyword, e.g. "FAQ",
// "Frequently Asked Questions About PSLE", or "Common Questions Parents Ask".
// Intentionally anchored to the start so headings like "Before the FAQ" or
// "Questions We Answered Last Week" that merely mention the word don't collapse
// the rest of the article into the FAQ section.
const FAQ_HEADING_PATTERN =
  /^(?:faqs?|frequently asked questions?|common questions?|questions?)\b/;

function isFaqSectionHeading(headingText: string): boolean {
  const normalized = headingText.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return FAQ_HEADING_PATTERN.test(normalized);
}

function stripInlineMarkdown(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2')
    .replace(/(^|[^_])_([^_\n]+)_/g, '$1$2')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeAnswerLine(rawLine: string): string {
  return rawLine
    .replace(/^\s*>\s?/, '')
    .replace(/^\s*[-*+]\s+/, '')
    .replace(/^\s*\d+\.\s+/, '')
    .trim();
}

/**
 * Parses Q/A pairs from a "Frequently Asked Questions" style section inside a markdown body.
 *
 * Detection rules:
 *  - The section is any H2 whose heading text matches "FAQ", "Frequently Asked Questions",
 *    "Common Questions", or otherwise contains the word "questions".
 *  - Within that section, each H3 is treated as a question and the non-heading text that follows
 *    it (up to the next H3 or H2) is joined into a single answer string.
 *  - Inline markdown (links, bold, italic, inline code) is stripped for clean schema text.
 *
 * Returns an empty array when the section is absent or no pairs can be extracted. Extracting the
 * pairs at render time keeps the MDX body as the single source of truth — no duplicate content
 * needs to be maintained in frontmatter.
 */
export function parseFaqPairsFromMarkdown(markdown: string): BlogFaqPair[] {
  if (typeof markdown !== 'string' || !markdown.trim()) {
    return [];
  }

  const lines = markdown.split(/\r?\n/);
  const pairs: BlogFaqPair[] = [];

  let inFaqSection = false;
  let currentQuestion: string | null = null;
  let answerLines: string[] = [];

  const commitCurrent = () => {
    if (currentQuestion) {
      const answer = stripInlineMarkdown(answerLines.join(' ')).trim();
      if (answer) {
        pairs.push({
          question: currentQuestion,
          answer,
        });
      }
    }
    currentQuestion = null;
    answerLines = [];
  };

  for (const line of lines) {
    const h2Match = /^##\s+(.+?)\s*$/.exec(line);
    if (h2Match) {
      commitCurrent();
      inFaqSection = isFaqSectionHeading(h2Match[1]);
      continue;
    }

    if (!inFaqSection) {
      continue;
    }

    const h3Match = /^###\s+(.+?)\s*$/.exec(line);
    if (h3Match) {
      commitCurrent();
      currentQuestion = stripInlineMarkdown(h3Match[1]);
      continue;
    }

    if (!currentQuestion) {
      continue;
    }

    const cleaned = normalizeAnswerLine(line);
    if (cleaned) {
      answerLines.push(cleaned);
    }
  }

  commitCurrent();
  return pairs;
}

function buildFaqPageJsonLd(
  faqPairs: readonly BlogFaqPair[]
): Record<string, unknown> | null {
  const mainEntity = faqPairs
    .map(pair => {
      const question =
        typeof pair.question === 'string' ? pair.question.trim() : '';
      const answer = typeof pair.answer === 'string' ? pair.answer.trim() : '';
      if (!question || !answer) {
        return null;
      }
      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      };
    })
    .filter(
      (
        entry
      ): entry is {
        '@type': string;
        name: string;
        acceptedAnswer: { '@type': string; text: string };
      } => entry !== null
    );

  if (mainEntity.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

export function buildBlogArticleSeoMetadata({
  brandName = 'DeepThink',
  siteOrigin = DEFAULT_SITE_ORIGIN,
  blogPost,
  socialImageUrl,
  publisherLogoUrl,
  faqPairs,
}: {
  brandName?: string;
  siteOrigin?: string;
  blogPost?: BlogPostInput;
  socialImageUrl?: string;
  publisherLogoUrl?: string;
  faqPairs?: readonly BlogFaqPair[];
} = {}): BlogArticleSeoMetadata {
  if (!blogPost) {
    throw new Error(
      'buildBlogArticleSeoMetadata requires a resolved blogPost record.'
    );
  }

  const normalizedSiteOrigin = normalizeSiteOrigin(siteOrigin);
  const canonicalRoutePath = normalizeBlogRoutePath(blogPost);
  const canonicalUrl = buildAbsoluteUrl(
    normalizedSiteOrigin,
    canonicalRoutePath
  );
  const title = `${blogPost.title} | ${brandName} Blog`;
  const description = getBlogArticleDescription(blogPost);
  const updatedDate = blogPost.updatedDate || blogPost.publishDate;
  const reviewedDate = blogPost.reviewedDate || updatedDate;
  const tags = Array.isArray(blogPost.tags)
    ? blogPost.tags.filter(Boolean)
    : [];
  const resolvedSocialImageUrl = resolveSocialImageUrl({
    siteOrigin: normalizedSiteOrigin,
    socialImageUrl,
  });
  const resolvedPublisherLogoUrl = buildAbsoluteUrl(
    normalizedSiteOrigin,
    publisherLogoUrl || socialImageUrl || DEFAULT_BLOG_SOCIAL_IMAGE_PATH
  );
  const authorType = blogPost.authorType || 'Organization';
  const reviewerType = blogPost.reviewerType || 'Organization';
  const authorEntity = buildSchemaEntity(
    {
      name: blogPost.authorName || brandName,
      type: authorType,
      description:
        authorType === 'Person'
          ? blogPost.authorCredentials || blogPost.authorRole
          : [blogPost.authorRole, blogPost.authorCredentials]
              .filter(Boolean)
              .join(' - '),
      jobTitle: blogPost.authorRole,
      url: blogPost.authorUrl,
    },
    normalizedSiteOrigin
  );
  const reviewerEntity = buildSchemaEntity(
    {
      name: blogPost.reviewerName,
      type: reviewerType,
      description:
        reviewerType === 'Person'
          ? blogPost.reviewerCredentials || blogPost.reviewerRole
          : [blogPost.reviewerRole, blogPost.reviewerCredentials]
              .filter(Boolean)
              .join(' - '),
      jobTitle: blogPost.reviewerRole,
      url: blogPost.reviewerUrl,
    },
    normalizedSiteOrigin
  );
  const publisherEntity = buildSchemaEntity(
    {
      name: brandName,
      type: 'Organization',
      url: normalizedSiteOrigin,
      description:
        blogPost.publisherDescription || `${brandName} blog publisher`,
    },
    normalizedSiteOrigin
  );
  if (publisherEntity) {
    publisherEntity.logo = {
      '@type': 'ImageObject',
      url: resolvedPublisherLogoUrl,
    };
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: normalizedSiteOrigin,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${normalizedSiteOrigin}/blog`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: blogPost.title,
        item: canonicalUrl,
      },
    ],
  };

  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blogPost.title,
    description,
    url: canonicalUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    datePublished: blogPost.publishDate,
    dateModified: updatedDate,
    image: resolvedSocialImageUrl,
    author: authorEntity,
    reviewedBy: reviewerEntity || undefined,
    publisher: publisherEntity,
    articleSection: blogPost.category,
    keywords: tags.length > 0 ? tags.join(', ') : undefined,
  };

  return {
    title,
    description,
    canonicalUrl,
    openGraph: {
      type: 'article',
      title,
      description,
      url: canonicalUrl,
      image: resolvedSocialImageUrl,
      imageAlt: `${blogPost.title} preview`,
      siteName: brandName,
      locale: 'en_SG',
      articlePublishedTime: blogPost.publishDate,
      articleModifiedTime: updatedDate,
      articleAuthor: blogPost.authorName || brandName,
      articleReviewedTime: reviewedDate,
      articleSection: blogPost.category,
      articleTags: tags,
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      image: resolvedSocialImageUrl,
      imageAlt: `${blogPost.title} preview`,
    },
    jsonLd: {
      blogPosting: blogPostingJsonLd,
      breadcrumb: breadcrumbJsonLd,
      ...(Array.isArray(faqPairs) && faqPairs.length > 0
        ? (() => {
            const faqPageJsonLd = buildFaqPageJsonLd(faqPairs);
            return faqPageJsonLd ? { faqPage: faqPageJsonLd } : {};
          })()
        : {}),
    },
  };
}
